import React, { useState, useCallback } from 'react';
import { Spin, Alert, message, Modal } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { usePW933BQuery } from './hooks/usePW933BQuery';
import QueryParams from './components/QueryParams';
import ResultTable from './components/ResultTable';
import { pw933bApi } from '../../services/pw933bApi';
import type { ExportResponse } from '../../services/pw933bApi';
import './PW933BForm.css';

const PW933BForm: React.FC = () => {
  const { loading, result, queried, noData, defaults, runQuery } = usePW933BQuery();

  const [year, setYear] = useState<number>(() => {
    return defaults?.year ?? new Date().getFullYear();
  });
  const [month, setMonth] = useState<number>(() => {
    return defaults?.month ?? (new Date().getMonth() + 1);
  });
  const [transferDate, setTransferDate] = useState<Dayjs | null>(dayjs());
  const [exporting, setExporting] = useState(false);

  // Update year/month when defaults load
  React.useEffect(() => {
    if (defaults) {
      setYear(defaults.year);
      setMonth(defaults.month);
    }
  }, [defaults]);

  const handleQuery = useCallback(() => {
    runQuery({ year, month });
  }, [year, month, runQuery]);

  const handleExport = useCallback(async () => {
    if (!transferDate) {
      message.warning('請先選擇轉帳日期');
      return;
    }

    // Confirm dialog (BR-V01)
    Modal.confirm({
      title: '列表確認',
      content: '確認所選範圍，開始產生撥款檔？',
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        setExporting(true);
        try {
          const resp: ExportResponse = await pw933bApi.exportFile({
            year,
            month,
            transferDate: transferDate.format('YYYY-MM-DD'),
          });

          // Download as text file
          const blob = new Blob([resp.content], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = resp.filename + '.txt';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          message.success(`撥款檔 ${resp.filename}.txt 已產生 (${resp.rows?.length ?? 0} 筆，總額 ${(resp.totalAmt ?? 0).toLocaleString()})`);
        } catch {
          message.error('匯出失敗，請稍後再試');
        } finally {
          setExporting(false);
        }
      },
    });
  }, [year, month, transferDate]);

  const handleYearChange = useCallback((v: number | null) => {
    if (v != null) setYear(v);
  }, []);

  const handleMonthChange = useCallback((v: string) => {
    setMonth(parseInt(v, 10));
  }, []);

  const handleTransferDateChange = useCallback((v: Dayjs | null) => {
    setTransferDate(v);
  }, []);

  return (
    <Spin spinning={loading || exporting}>
      <div className="pw933b-container max-w-7xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-4">
          薪資撥款清冊 (pw933bf1)
        </h2>

        <QueryParams
          year={year}
          month={month}
          transferDate={transferDate}
          loading={loading}
          exporting={exporting}
          onYearChange={handleYearChange}
          onMonthChange={handleMonthChange}
          onTransferDateChange={handleTransferDateChange}
          onQuery={handleQuery}
          onExport={handleExport}
        />

        {queried && !noData && result && (
          <ResultTable result={result} />
        )}

        {queried && noData && (
          <Alert
            message="查無資料"
            description="查無符合條件的薪資撥款資料！請確認年度月份是否正確。"
            type="info"
            showIcon
          />
        )}
      </div>
    </Spin>
  );
};

export default PW933BForm;
